import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Card,
    CardBody,
    Flex,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    Badge,
} from '@chakra-ui/react';
import { FaLock, FaChartLine, FaUsers, FaExclamationTriangle, FaBrain, FaBoxOpen, FaCheckCircle } from 'react-icons/fa';
import ThemeContext from '../../context/ThemeContext';

function DashboardFree() {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const navigate = useNavigate();
    const { themeData } = useContext(ThemeContext);

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast - following Dashboard.js pattern
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';

    // Overlay background - improved for dark theme
    const overlayBg = isDarkTheme ? 'rgba(26, 32, 44, 0.92)' : 'rgba(255, 255, 255, 0.92)';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const dashedBorderColor = isDarkTheme ? accentColor : primaryColor;

    // Info banner background - improved transparency for dark theme
    const infoBannerBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '15';

    // Button colors
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Status colors - lighter for dark theme
    const successColor = isDarkTheme ? '#68D391' : '#38A169';
    const errorColor = isDarkTheme ? '#FC8181' : '#E53E3E';
    const warningColor = isDarkTheme ? '#B794F4' : '#805AD5';

    // Placeholder background
    const placeholderBg = isDarkTheme ? 'rgba(45, 55, 72, 0.4)' : 'gray.100';

    return (
        <Container maxW="container.xl" py={10} position="relative">
            {/* Lock Overlay */}
            <Flex
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                zIndex={5}
                bg={overlayBg}
                backdropFilter="blur(3px)"
                borderRadius="xl"
                justify="center"
                align="center"
                flexDirection="column"
                textAlign="center"
                border="3px dashed"
                borderColor={dashedBorderColor}
            >
                <Icon as={FaLock} boxSize={16} color={isDarkTheme ? accentColor : primaryColor} mb={4} />
                <Heading size="xl" mb={3} color={headingTextColor}>
                    Premium Dashboard Insights
                </Heading>
                <Text color={bodyTextColor} maxW="md" mb={6} fontSize="lg" fontWeight="medium">
                    Unlock powerful customer analytics, behavioral insights, risk assessments, and product recommendations with FinanSee Premium.
                </Text>
                <Button
                    size="lg"
                    bg={primaryColor}
                    color="white"
                    onClick={() => navigate('/subscription')}
                    mb={3}
                    px={8}
                    py={6}
                    fontSize="lg"
                    _hover={{ bg: buttonHoverBg }}
                >
                    Upgrade to Premium
                </Button>
                <Button 
                    variant="ghost" 
                    color={isDarkTheme ? accentColor : primaryColor} 
                    onClick={onOpen} 
                    size="md"
                    _hover={{ bg: isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'gray.100' }}
                >
                    What's Included?
                </Button>
            </Flex>

            {/* Blurred Dashboard Preview */}
            <Box filter="blur(1.5px)" opacity={0.6} pointerEvents="none">
                <VStack spacing={8} align="stretch">
                    {/* Header */}
                    <Box>
                        <Heading size="xl" color={headingTextColor} mb={2}>
                            <HStack spacing={3}>
                                <Icon as={FaChartLine} />
                                <Text>Portfolio Dashboard</Text>
                            </HStack>
                        </Heading>
                        <Text color={bodyTextColor} fontSize="md">
                            Real-time insights and analytics for your customer portfolio
                        </Text>
                    </Box>

                    {/* Summary Statistics */}
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                        <Card 
                            bg={cardBg} 
                            borderRadius="xl" 
                            boxShadow="md" 
                            border="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody p={5}>
                                <Stat>
                                    <HStack spacing={2} mb={1}>
                                        <Icon as={FaUsers} color={isDarkTheme ? accentColor : primaryColor} boxSize={5} />
                                        <StatLabel fontSize="sm" color={bodyTextColor}>
                                            Total Customers
                                        </StatLabel>
                                    </HStack>
                                    <StatNumber fontSize="3xl" color={isDarkTheme ? accentColor : primaryColor} fontWeight="bold">
                                        127
                                    </StatNumber>
                                </Stat>
                            </CardBody>
                        </Card>

                        <Card 
                            bg={cardBg} 
                            borderRadius="xl" 
                            boxShadow="md" 
                            border="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody p={5}>
                                <Stat>
                                    <HStack spacing={2} mb={1}>
                                        <Icon as={FaExclamationTriangle} color={errorColor} boxSize={5} />
                                        <StatLabel fontSize="sm" color={bodyTextColor}>
                                            High Risk
                                        </StatLabel>
                                    </HStack>
                                    <StatNumber fontSize="3xl" color={errorColor} fontWeight="bold">
                                        23
                                    </StatNumber>
                                </Stat>
                            </CardBody>
                        </Card>

                        <Card 
                            bg={cardBg} 
                            borderRadius="xl" 
                            boxShadow="md" 
                            border="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody p={5}>
                                <Stat>
                                    <HStack spacing={2} mb={1}>
                                        <Icon as={FaBrain} color={warningColor} boxSize={5} />
                                        <StatLabel fontSize="sm" color={bodyTextColor}>
                                            Bias Score
                                        </StatLabel>
                                    </HStack>
                                    <StatNumber fontSize="3xl" color={warningColor} fontWeight="bold">
                                        68
                                    </StatNumber>
                                </Stat>
                            </CardBody>
                        </Card>

                        <Card 
                            bg={cardBg} 
                            borderRadius="xl" 
                            boxShadow="md" 
                            border="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody p={5}>
                                <Stat>
                                    <HStack spacing={2} mb={1}>
                                        <Icon as={FaCheckCircle} color={successColor} boxSize={5} />
                                        <StatLabel fontSize="sm" color={bodyTextColor}>
                                            Priority Score
                                        </StatLabel>
                                    </HStack>
                                    <StatNumber fontSize="3xl" color={successColor} fontWeight="bold">
                                        7.8
                                    </StatNumber>
                                </Stat>
                            </CardBody>
                        </Card>
                    </SimpleGrid>

                    {/* Feature Preview Cards */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <HStack spacing={2} mb={4}>
                                    <Icon as={FaBrain} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                    <Heading size="md" color={headingTextColor}>
                                        Behavioral Bias Analysis
                                    </Heading>
                                </HStack>
                                <Text color={bodyTextColor} mb={4}>
                                    Identify and analyze customer behavioral patterns
                                </Text>
                                <Box bg={placeholderBg} h="200px" borderRadius="md" />
                            </CardBody>
                        </Card>

                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <HStack spacing={2} mb={4}>
                                    <Icon as={FaBoxOpen} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                    <Heading size="md" color={headingTextColor}>
                                        Product Recommendations
                                    </Heading>
                                </HStack>
                                <Text color={bodyTextColor} mb={4}>
                                    AI-powered product suggestions for each customer
                                </Text>
                                <Box bg={placeholderBg} h="200px" borderRadius="md" />
                            </CardBody>
                        </Card>

                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <HStack spacing={2} mb={4}>
                                    <Icon as={FaExclamationTriangle} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                    <Heading size="md" color={headingTextColor}>
                                        Risk Assessment
                                    </Heading>
                                </HStack>
                                <Text color={bodyTextColor} mb={4}>
                                    Comprehensive risk profiling and tolerance analysis
                                </Text>
                                <Box bg={placeholderBg} h="200px" borderRadius="md" />
                            </CardBody>
                        </Card>

                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <HStack spacing={2} mb={4}>
                                    <Icon as={FaUsers} color={isDarkTheme ? accentColor : primaryColor} boxSize={6} />
                                    <Heading size="md" color={headingTextColor}>
                                        Customer Segments
                                    </Heading>
                                </HStack>
                                <Text color={bodyTextColor} mb={4}>
                                    Behavioral segmentation and distribution insights
                                </Text>
                                <Box bg={placeholderBg} h="200px" borderRadius="md" />
                            </CardBody>
                        </Card>
                    </SimpleGrid>

                    {/* Additional Info Banner */}
                    <Card 
                        bg={infoBannerBg} 
                        borderRadius="xl" 
                        boxShadow="sm" 
                        border="1px" 
                        borderColor={isDarkTheme ? 'rgba(66, 153, 225, 0.4)' : primaryColor}
                    >
                        <CardBody p={6}>
                            <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                                <VStack align="start" spacing={1}>
                                    <Heading size="sm" color={headingTextColor}>
                                        Unlock Full Analytics Suite
                                    </Heading>
                                    <Text fontSize="sm" color={bodyTextColor}>
                                        Get access to all premium features and insights
                                    </Text>
                                </VStack>
                                <Badge colorScheme="blue" fontSize="lg" px={4} py={2}>
                                    Premium Feature
                                </Badge>
                            </Flex>
                        </CardBody>
                    </Card>
                </VStack>
            </Box>

            {/* What's Included Modal */}
            <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
                <ModalOverlay backdropFilter="blur(8px)" bg={isDarkTheme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)'} />
                <ModalContent 
                    borderRadius="xl" 
                    bg={cardBg} 
                    borderWidth="1px" 
                    borderColor={borderColor}
                >
                    <ModalHeader>
                        <HStack spacing={2}>
                            <Icon as={FaLock} color={isDarkTheme ? accentColor : primaryColor} />
                            <Text color={headingTextColor}>What's Included in FinanSee Premium</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton color={headingTextColor} />
                    <ModalBody>
                        <VStack align="start" spacing={4} fontSize="md" color={bodyTextColor}>
                            <HStack spacing={3} align="start">
                                <Icon as={FaBrain} color={isDarkTheme ? accentColor : primaryColor} mt={1} />
                                <Text>
                                    <Text as="span" fontWeight="bold" color={labelTextColor}>Behavioral Bias Analysis:</Text> Deep insights into customer psychology and decision-making patterns
                                </Text>
                            </HStack>
                            <HStack spacing={3} align="start">
                                <Icon as={FaBoxOpen} color={isDarkTheme ? accentColor : primaryColor} mt={1} />
                                <Text>
                                    <Text as="span" fontWeight="bold" color={labelTextColor}>AI Product Recommendations:</Text> Personalized product suggestions based on behavioral profiles
                                </Text>
                            </HStack>
                            <HStack spacing={3} align="start">
                                <Icon as={FaExclamationTriangle} color={isDarkTheme ? accentColor : primaryColor} mt={1} />
                                <Text>
                                    <Text as="span" fontWeight="bold" color={labelTextColor}>Risk Profiling:</Text> Comprehensive risk tolerance and capacity analysis
                                </Text>
                            </HStack>
                            <HStack spacing={3} align="start">
                                <Icon as={FaUsers} color={isDarkTheme ? accentColor : primaryColor} mt={1} />
                                <Text>
                                    <Text as="span" fontWeight="bold" color={labelTextColor}>Customer Segmentation:</Text> Advanced behavioral segmentation and portfolio insights
                                </Text>
                            </HStack>
                            <HStack spacing={3} align="start">
                                <Icon as={FaChartLine} color={isDarkTheme ? accentColor : primaryColor} mt={1} />
                                <Text>
                                    <Text as="span" fontWeight="bold" color={labelTextColor}>Priority Scoring:</Text> Identify which customers need immediate attention
                                </Text>
                            </HStack>
                            <HStack spacing={3} align="start">
                                <Icon as={FaCheckCircle} color={isDarkTheme ? accentColor : primaryColor} mt={1} />
                                <Text>
                                    <Text as="span" fontWeight="bold" color={labelTextColor}>Real-time Analytics:</Text> Live updates and comprehensive reporting
                                </Text>
                            </HStack>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            bg={primaryColor}
                            color="white"
                            onClick={() => {
                                onClose();
                                navigate('/subscription');
                            }}
                            _hover={{ bg: buttonHoverBg }}
                            size="lg"
                        >
                            Upgrade to Premium
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Container>
    );
}

export default DashboardFree;